import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

//Actions
import { getCampaignMediaByCity } from "../../../actions/campaign-media/CampaignMediaActions";
import {
  getCityOverview,
  downloadCityPdfReport,
  downloadCityMediasPdfReportsZip,
  downloadCityMediasSinglePdfReport,
  openDownloadCampaignCityReportForm,
  closeDownloadCampaignCityReportForm,
} from "../../../actions/campaign-report/CampaignCityReportActions";
import { cancelAppliedFilters } from "../../../actions/filter/FilterActions";

// Custom Hooks
import {
  useCityMediasPdfReportsZipDownload,
  useCityMediasSinglePdfReportDownload,
  useCityPdfReportDownload,
} from "../../../hooks/ReportsDownloadHooks";

//Utils and Constants
import {
  CityPdfReportConstants,
  DefaultPagination,
  FormDataTargets,
  MapZoom,
} from "../../../constants/GeneralConstants";

//Components
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import Spinner from "../../../components/spinner/Spinner";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import CampaignMediaForm from "../../../components/campaign-media-form/CampaignMediaForm";
import { CampaignCityPerformance } from "../../../mavin/components/campaign-city-performance/CampaignCityPerformance";
import DownloadReportsForm from "../../../components/download-reports-from/DownloadReportsForm";
import CampaignTgViewButton from "../../../components/campaign-tg-form/CampaignTgViewButton";

// Urls
import { RedirectTo } from "../../../urls/PageLinksURL";

// Pages and Section
import CampaignCityMapView from "./CampaignCityMapView";
import CityViewSiteOptions from "./CityViewSiteOptions";
import CampaignCityViewMediaTable from "./CampaignCityViewMediaTable";

//Css
import "./CampaignCityViewPage.scss";

// Page Components
function DownloadReport() {
  const dispatch = useDispatch();

  // Selector
  const downloadingReport = useSelector(
    (state) => state.campaignCityReport.downloadCityPdfReportLoading
  );

  const downloadCityMediasPdfReportsZipLoading = useSelector(
    (state) => state.campaignCityReport.downloadCityMediasPdfReportsZipLoading
  );

  const downloadCityMediasSinglePdfReportLoading = useSelector(
    (state) => state.campaignCityReport.downloadCityMediasSinglePdfReportLoading
  );

  if (
    downloadingReport ||
    downloadCityMediasPdfReportsZipLoading ||
    downloadCityMediasSinglePdfReportLoading
  ) {
    return <Spinner className="spinner-border mx-3" />;
  }

  return (
    <p
      className="text-primary cursor-pointer my-2"
      data-toggle="modal"
      data-target={`#${FormDataTargets.downloadReportsForm}`}
      onClick={() => dispatch(openDownloadCampaignCityReportForm())}
      data-html2canvas-ignore="true"
    >
      Download City Reports
    </p>
  );
}

function PageHeaderSection({ campaign = {}, citySelected = {} }) {
  const { cityName } = citySelected;
  const {
    title = "",
    // targetGroupId,
    // targetGroupName
  } = campaign;

  // tg name
  // const tgName = targetGroupId ? targetGroupName : "NA";

  const pageTitle = title + " :: " + cityName;

  const pageActions = (
    <div className="d-flex align-items-center">
      <CampaignTgViewButton selectedTgData={campaign} />
      <DownloadReport />
    </div>
  );

  return <PageHeader title={pageTitle} actions={pageActions} shadow={true} />;
}

/**
 * Page
 */
export default function CampaignCityViewPage() {
  const { id, cityId } = useParams();

  // state
  const [mapCenter, setMapCenter] = useState([]);
  const [mapZoom, setMapZoom] = useState(MapZoom.zoomLevel9);

  //Selector States
  const citySummaryLoading = useSelector(
    (state) => state.campaignCityReport.citySummaryLoading
  );
  const { campaign = {}, summary: citySummary = {} } = useSelector(
    (state) => state.campaignCityReport.citySummary
  );
  const { population } = citySummary || {};

  const {
    targetGroupId = "",
    budget = 0,
    spend = 0,
    infoByCity = {},
    title = "",
    campaignType = "",
  } = campaign || {};
  const cityInfo = infoByCity?.[cityId] || {};
  const { mediaCount } = cityInfo || {};
  const isMediaSitesPresent = mediaCount > 0;

  // City media summary
  const cityMediaLoading = useSelector(
    (state) => state.campaignMedia.cityLoading
  );
  const cityMediaSummary = useSelector(
    (state) => state.campaignMedia.citySummary
  );
  const cityMediaSummaryResult = cityMediaSummary.result || {};
  const { items = [], pagination = {} } = cityMediaSummaryResult;

  // Create Media Modal Key
  const openMediaForm = useSelector((state) => state.campaignMedia.openModal);

  // download reports form
  const openDownloadCampaignCityReportModal = useSelector(
    (state) => state.campaignCityReport.openDownloadCampaignCityReportModal
  );

  const citySelected = {
    cityId: cityId,
    cityName: citySummary?.cityName || cityId,
  };
  const cityName = citySelected?.cityName || "";

  // Link to pass in CampaignSidebar
  const redirectLinkObj = {
    campaignOverviewLink: RedirectTo.campaignViewPageUrl.replace(
      ":campaignId",
      id
    ),
    citySummaryLink: RedirectTo.campaignCityViewPageUrl.replace(
      ":campaignId",
      id
    ),
  };

  // Pagination
  const { pageNumber, pageSize } = DefaultPagination;

  //Dispatch
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(cancelAppliedFilters());

    dispatch(getCityOverview(id, cityId));
    dispatch(getCampaignMediaByCity(id, cityId, pageNumber, pageSize));
  }, [dispatch, id, cityId, pageSize]);

  // pdf report download effects
  useCityPdfReportDownload(title, cityName);
  useCityMediasSinglePdfReportDownload(title, cityName);
  useCityMediasPdfReportsZipDownload(title, cityName);

  // Functions
  function loadCityMedia() {
    dispatch(getCampaignMediaByCity(id, cityId, pageNumber, pageSize, true));
  }

  //checks for page loading
  if (cityMediaLoading || citySummaryLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  // report download function
  function handleDownloadReportSubmit(
    isSentViaEmail,
    emailIds,
    selectedReport,
    radius
  ) {
    // city pdf report
    if (selectedReport === CityPdfReportConstants.cityPdf) {
      dispatch(
        downloadCityPdfReport(
          id,
          cityId,
          mapCenter,
          mapZoom,
          isSentViaEmail,
          emailIds
        )
      );
      return;
    }

    // city medias single pdf report
    if (selectedReport === CityPdfReportConstants.cityMediasSinglePdf) {
      dispatch(
        downloadCityMediasSinglePdfReport(
          id,
          cityId,
          radius,
          isSentViaEmail,
          emailIds
        )
      );
      return;
    }

    // city medias single pdf report
    if (selectedReport === CityPdfReportConstants.cityMediasZip) {
      dispatch(
        downloadCityMediasPdfReportsZip(
          id,
          cityId,
          radius,
          isSentViaEmail,
          emailIds
        )
      );
      return;
    }
  }

  return (
    <>
      {/* Sidebar */}
      <CampaignSidebar
        campaign={campaign}
        citySelected={citySelected}
        redirectLinkObj={redirectLinkObj}
      />

      <div className="content-wrapper" id="cont-city-details">
        {/* Page Header */}
        <PageHeaderSection campaign={campaign} citySelected={citySelected} />

        <div className="page-content">
          {/* Performance Card */}
          <CampaignCityPerformance
            campaign={campaign}
            citySummary={citySummary}
            budget={budget}
            spend={spend}
          />

          {/* City Map View */}
          <CampaignCityMapView
            mediaList={items}
            targetGroupId={targetGroupId}
            mapCenter={mapCenter}
            setMapCenter={setMapCenter}
            mapZoom={mapZoom}
            setMapZoom={setMapZoom}
          />

          {/* City Site Option */}
          <CityViewSiteOptions
            loadCityMedia={loadCityMedia}
            targetGroupId={targetGroupId}
            campaignType={campaignType}
            filterPageSize={pageSize}
          />

          {/* Media Table */}
          <CampaignCityViewMediaTable
            mediaList={items}
            pagination={pagination}
            loadCityMedia={loadCityMedia}
            cityPopulation={population}
          />
        </div>
      </div>

      {/** Modals */}
      {openMediaForm && (
        /* Modal :: Create CampaignMedia */
        <CampaignMediaForm campaignId={id} cityId={cityId} />
      )}

      {/* Download Campaign City Report */}
      {openDownloadCampaignCityReportModal && (
        <DownloadReportsForm
          handleSubmit={handleDownloadReportSubmit}
          handleClose={() => dispatch(closeDownloadCampaignCityReportForm())}
          isCityReportForm={true}
          isMediaSitesPresent={isMediaSitesPresent}
        />
      )}
    </>
  );
}
